import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Avatar,
  LinearProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Download as DownloadIcon,
  PictureAsPdf as PdfIcon,
  GridOn as ExcelIcon,
  CalendarMonth as CalendarIcon,
  TrendingUp as TrendIcon,
  People as PeopleIcon,
  MedicalServices as MedicalIcon,
  Assessment as ReportIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import { RootState, AppDispatch } from '../../../store';
import axios from 'axios';

interface CompletedAppointment {
  id: number;
  bookingNumber: string;
  patientName: string;
  patientType: string;
  officerName: string;
  officerDesignation: string;
  appointmentDate: string;
  appointmentTime: string;
  visitDuration: number;
  diagnosis: string;
  medicinesCount: number;
  prescriptionNumber: string;
  reportNumber: string;
  completedAt: string;
  visitAddress: string;
  followUpRequired: boolean;
}

interface ReportStats {
  totalAppointments: number;
  homeVisits: number;
  avgVisitDuration: number;
  uniquePatients: number;
  followUps: number;
  totalMedicines: number;
  topDiagnoses: { diagnosis: string; count: number }[];
  monthlyTrend: { month: string; count: number }[];
}

/**
 * Doctor Reports Page
 * View completed appointments and generate custom reports
 */
const DoctorReportsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);

  const [loading, setLoading] = useState(true);
  const [appointments, setAppointments] = useState<CompletedAppointment[]>([]);
  const [stats, setStats] = useState<ReportStats | null>(null);

  // Filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [reportType, setReportType] = useState<'monthly' | 'custom'>('monthly');

  // View Dialog
  const [selectedAppointment, setSelectedAppointment] = useState<CompletedAppointment | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || user?.role !== 'GOVERNMENT_DOCTOR') {
      navigate('/login');
      return;
    }

    // Set default dates (current month)
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    setStartDate(firstDay.toISOString().split('T')[0]);
    setEndDate(lastDay.toISOString().split('T')[0]);
    setSelectedMonth(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);

    fetchCompletedAppointments();
  }, [isAuthenticated, user, navigate]);

  const fetchCompletedAppointments = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const params = new URLSearchParams();

      if (reportType === 'custom' && startDate && endDate) {
        params.append('startDate', startDate);
        params.append('endDate', endDate);
      } else if (selectedMonth) {
        const [year, month] = selectedMonth.split('-');
        const firstDay = new Date(parseInt(year), parseInt(month) - 1, 1);
        const lastDay = new Date(parseInt(year), parseInt(month), 0);
        params.append('startDate', firstDay.toISOString().split('T')[0]);
        params.append('endDate', lastDay.toISOString().split('T')[0]);
      }

      // Fetch completed appointments
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL || 'http://localhost:8082/api'}/v1/appointments/doctor/my?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        // Filter only CLOSED appointments
        const completed = response.data.data.filter((apt: any) =>
          apt.status?.toUpperCase() === 'CLOSED'
        );
        setAppointments(completed);
        calculateStats(completed);
      }
    } catch (error) {
      console.error('Failed to fetch appointments:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (data: CompletedAppointment[]) => {
    const uniquePatients = new Set(data.map(apt => apt.patientName)).size;
    const totalDuration = data.reduce((sum, apt) => sum + (apt.visitDuration || 0), 0);
    const homeVisits = data.filter(apt => apt.visitAddress).length;
    const followUps = data.filter(apt => apt.followUpRequired).length;
    const totalMeds = data.reduce((sum, apt) => sum + (apt.medicinesCount || 0), 0);

    // Top diagnoses
    const diagnosisCount: Record<string, number> = {};
    data.forEach(apt => {
      if (apt.diagnosis) {
        diagnosisCount[apt.diagnosis] = (diagnosisCount[apt.diagnosis] || 0) + 1;
      }
    });
    const topDiagnoses = Object.entries(diagnosisCount)
      .map(([diagnosis, count]) => ({ diagnosis, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    setStats({
      totalAppointments: data.length,
      homeVisits,
      avgVisitDuration: data.length ? Math.round(totalDuration / data.length) : 0,
      uniquePatients,
      followUps,
      totalMedicines: totalMeds,
      topDiagnoses,
      monthlyTrend: [],
    });
  };

  const handleDownloadPDF = () => {
    // Generate PDF report
    const reportContent = generateReportContent();
    const blob = new Blob([reportContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Doctor_Report_${selectedMonth || 'custom'}_${new Date().toISOString().split('T')[0]}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadExcel = () => {
    // Generate CSV for Excel
    const headers = ['Booking No', 'Patient Name', 'Date', 'Time', 'Duration (min)', 'Diagnosis', 'Medicines', 'Completed At'];
    const rows = appointments.map(apt => [
      apt.bookingNumber,
      apt.patientName,
      apt.appointmentDate,
      apt.appointmentTime,
      apt.visitDuration || '-',
      apt.diagnosis || '-',
      apt.medicinesCount || 0,
      apt.completedAt,
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Doctor_Report_${selectedMonth || 'custom'}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const generateReportContent = () => {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Doctor Report - ${user?.fullName}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { color: #1a73e8; }
          .header { border-bottom: 2px solid #1a73e8; padding-bottom: 10px; margin-bottom: 20px; }
          .stats { display: flex; gap: 20px; margin-bottom: 30px; }
          .stat-box { background: #f5f5f5; padding: 15px; border-radius: 8px; min-width: 150px; }
          table { width: 100%; border-collapse: collapse; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background: #1a73e8; color: white; }
          tr:nth-child(even) { background: #f9f9f9; }
          .footer { margin-top: 30px; font-size: 12px; color: #666; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Doctor Performance Report</h1>
          <p><strong>Doctor:</strong> ${user?.fullName}</p>
          <p><strong>Period:</strong> ${reportType === 'monthly' ? selectedMonth : `${startDate} to ${endDate}`}</p>
          <p><strong>Generated On:</strong> ${new Date().toLocaleString()}</p>
        </div>

        <div class="stats">
          <div class="stat-box">
            <h3>${stats?.totalAppointments || 0}</h3>
            <p>Total Appointments</p>
          </div>
          <div class="stat-box">
            <h3>${stats?.homeVisits || 0}</h3>
            <p>Home Visits</p>
          </div>
          <div class="stat-box">
            <h3>${stats?.uniquePatients || 0}</h3>
            <p>Unique Patients</p>
          </div>
          <div class="stat-box">
            <h3>${stats?.avgVisitDuration || 0} min</h3>
            <p>Avg Duration</p>
          </div>
        </div>

        <h2>Completed Appointments</h2>
        <table>
          <thead>
            <tr>
              <th>Booking No</th>
              <th>Patient Name</th>
              <th>Date</th>
              <th>Time</th>
              <th>Duration</th>
              <th>Diagnosis</th>
              <th>Medicines</th>
            </tr>
          </thead>
          <tbody>
            ${appointments.map(apt => `
              <tr>
                <td>${apt.bookingNumber}</td>
                <td>${apt.patientName}</td>
                <td>${apt.appointmentDate}</td>
                <td>${apt.appointmentTime}</td>
                <td>${apt.visitDuration || '-'} min</td>
                <td>${apt.diagnosis || '-'}</td>
                <td>${apt.medicinesCount || 0}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>

        <div class="footer">
          <p>This report is generated by Civilhealth Healthcare System</p>
        </div>
      </body>
      </html>
    `;
  };

  const handlePrint = () => {
    window.print();
  };

  const handleViewAppointment = (apt: CompletedAppointment) => {
    setSelectedAppointment(apt);
    setViewDialogOpen(true);
  };

  return (
    <Box sx={{ p: 3, bgcolor: 'transparent' , minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b' }}>
            Reports & Analytics
          </Typography>
          <Typography variant="body1" color="text.secondary">
            View completed appointments and generate custom reports
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchCompletedAppointments}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<PdfIcon />}
            onClick={handleDownloadPDF}
            disabled={appointments.length === 0}
          >
            Download PDF
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<ExcelIcon />}
            onClick={handleDownloadExcel}
            disabled={appointments.length === 0}
          >
            Download Excel
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterIcon /> Report Filters
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Report Type</InputLabel>
              <Select
                value={reportType}
                label="Report Type"
                onChange={(e) => setReportType(e.target.value as 'monthly' | 'custom')}
              >
                <MenuItem value="monthly">Monthly Report</MenuItem>
                <MenuItem value="custom">Custom Date Range</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {reportType === 'monthly' ? (
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="month"
                label="Select Month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          ) : (
            <>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  type="date"
                  label="Start Date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  type="date"
                  label="End Date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </>
          )}

          <Grid item xs={12} md={3}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<ReportIcon />}
              onClick={fetchCompletedAppointments}
              sx={{ height: 56 }}
            >
              Generate Report
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <LinearProgress />
      ) : (
        <>
          {/* Statistics Cards */}
          {stats && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} md={2}>
                <Card sx={{ borderRadius: 2, height: '100%' }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    {/* HIGH CONTRAST: Light background + Dark icon */}
                    <Avatar sx={{ bgcolor: 'transparent' , color: '#1D4ED8', mx: 'auto', mb: 1, border: '2px solid rgba(29, 78, 216, 0.2)' }}>
                      <MedicalIcon />
                    </Avatar>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#1A1A2E' }}>{stats.totalAppointments}</Typography>
                    <Typography variant="body2" color="text.secondary">Completed</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} md={2}>
                <Card sx={{ borderRadius: 2, height: '100%' }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    {/* HIGH CONTRAST: Light background + Dark icon */}
                    <Avatar sx={{ bgcolor: 'transparent' , color: '#15803D', mx: 'auto', mb: 1, border: '2px solid rgba(21, 128, 61, 0.2)' }}>
                      <PeopleIcon />
                    </Avatar>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#1A1A2E' }}>{stats.uniquePatients}</Typography>
                    <Typography variant="body2" color="text.secondary">Patients</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} md={2}>
                <Card sx={{ borderRadius: 2, height: '100%' }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    {/* HIGH CONTRAST: Light background + Dark icon */}
                    <Avatar sx={{ bgcolor: 'transparent' , color: '#B45309', mx: 'auto', mb: 1, border: '2px solid rgba(180, 83, 9, 0.2)' }}>
                      <CalendarIcon />
                    </Avatar>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#1A1A2E' }}>{stats.homeVisits}</Typography>
                    <Typography variant="body2" color="text.secondary">Home Visits</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} md={2}>
                <Card sx={{ borderRadius: 2, height: '100%' }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    {/* HIGH CONTRAST: Light background + Dark icon */}
                    <Avatar sx={{ bgcolor: 'transparent' , color: '#DC2626', mx: 'auto', mb: 1, border: '2px solid rgba(220, 38, 38, 0.2)' }}>
                      <TrendIcon />
                    </Avatar>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#1A1A2E' }}>{stats.avgVisitDuration}</Typography>
                    <Typography variant="body2" color="text.secondary">Avg Minutes</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} md={2}>
                <Card sx={{ borderRadius: 2, height: '100%' }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    {/* HIGH CONTRAST: Light background + Dark icon */}
                    <Avatar sx={{ bgcolor: 'transparent' , color: '#1D4ED8', mx: 'auto', mb: 1, border: '2px solid rgba(29, 78, 216, 0.2)' }}>
                      <MedicalIcon />
                    </Avatar>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#1A1A2E' }}>{stats.totalMedicines}</Typography>
                    <Typography variant="body2" color="text.secondary">Medicines Given</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} md={2}>
                <Card sx={{ borderRadius: 2, height: '100%' }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    {/* HIGH CONTRAST: Light background + Dark icon */}
                    <Avatar sx={{ bgcolor: 'transparent' , color: '#15803D', mx: 'auto', mb: 1, border: '2px solid rgba(21, 128, 61, 0.2)' }}>
                      <CalendarIcon />
                    </Avatar>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#1A1A2E' }}>{stats.followUps}</Typography>
                    <Typography variant="body2" color="text.secondary">Follow-ups</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Top Diagnoses */}
          {stats && stats.topDiagnoses.length > 0 && (
            <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Top Diagnoses</Typography>
              <Grid container spacing={1}>
                {stats.topDiagnoses.map((item, index) => (
                  <Grid item xs={12} md={4} key={index}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1.5, bgcolor: 'transparent' , borderRadius: 1 }}>
                      <Typography variant="body2">{item.diagnosis}</Typography>
                      <Chip label={item.count} size="small" color="primary" />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          )}

          {/* Completed Appointments Table */}
          <Paper sx={{ borderRadius: 2 }}>
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                Completed Appointments ({appointments.length})
              </Typography>
              <Button startIcon={<PrintIcon />} onClick={handlePrint}>
                Print
              </Button>
            </Box>
            <Divider />
            <TableContainer sx={{ maxHeight: 500 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Booking No</TableCell>
                    <TableCell>Patient Name</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Time</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Diagnosis</TableCell>
                    <TableCell>Medicines</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {appointments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No completed appointments found for selected period</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    appointments.map((apt) => (
                      <TableRow key={apt.id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{apt.bookingNumber}</Typography>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{apt.patientName}</Typography>
                            <Typography variant="caption" color="text.secondary">{apt.officerDesignation}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{apt.appointmentDate}</TableCell>
                        <TableCell>{apt.appointmentTime}</TableCell>
                        <TableCell>
                          <Chip label={`${apt.visitDuration || '-'} min`} size="small" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {apt.diagnosis || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={apt.medicinesCount || 0} size="small" color="success" />
                        </TableCell>
                        <TableCell>
                          <Tooltip title="View Details">
                            <IconButton size="small" onClick={() => handleViewAppointment(apt)}>
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}

      {/* View Appointment Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Appointment Details - {selectedAppointment?.bookingNumber}</DialogTitle>
        <DialogContent dividers>
          {selectedAppointment && (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Patient Name</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedAppointment.patientName}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Officer Designation</Typography>
                <Typography variant="body1">{selectedAppointment.officerDesignation}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Appointment Date</Typography>
                <Typography variant="body1">{selectedAppointment.appointmentDate}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Time</Typography>
                <Typography variant="body1">{selectedAppointment.appointmentTime}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Visit Duration</Typography>
                <Typography variant="body1">{selectedAppointment.visitDuration || '-'} minutes</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Medicines Prescribed</Typography>
                <Typography variant="body1">{selectedAppointment.medicinesCount || 0}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Diagnosis</Typography>
                <Typography variant="body1">{selectedAppointment.diagnosis || '-'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Visit Address</Typography>
                <Typography variant="body1">{selectedAppointment.visitAddress || '-'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Report Number</Typography>
                <Typography variant="body1">{selectedAppointment.reportNumber || '-'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Prescription Number</Typography>
                <Typography variant="body1">{selectedAppointment.prescriptionNumber || '-'}</Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DoctorReportsPage;
